<script>
    import tinyPumperIncidentsService from '@/services/tinyPumperIncidents.service';
    import ActionsDropdown from '@/components/shared/ActionsDropdown';
    import { SENSOR_SNOOZE_REASON_LABELS, SensorSnoozeReason } from '@/enums/TinyPumper';

    const OWNER_LABELS = Object.freeze({ Engineering: 'Engineering', Vendor: 'LevelCon' });

    /**
     * The ops coordinator's morning: which clients need a call or a visit today, what is already in
     * someone's hands, and what engineering or LevelCon owns. Lanes and copy come from GET today.
     */
    export default {
        name: 'TinyPumperToday',
        props: {
            busyIncidentId: {
                type: Number,
                default: null
            }
        },
        emits: ['book-visit', 'wait-on-client', 'handled', 'decommission', 'open-levelcon', 'show-incident', 'open-alerting'],
        components: { ActionsDropdown },
        data() {
            return {
                // null until answered: "not loaded" must never render as "nothing needs you".
                today: null,
                isLoading: false,
                hasFailed: false,
                isWaitingOpen: false
            };
        },
        computed: {
            getClients() {
                return this.today?.needsYou || [];
            },
            getWaiting() {
                return this.today?.waiting || [];
            },
            getNotYours() {
                return this.today?.notYours || [];
            },
            isAllClear() {
                return !!this.today && this.getClients.length === 0;
            },
            getHeadline() {
                const count = this.getClients.length;
                if (count === 0) return 'Nothing needs you today';

                return `${count} ${count === 1 ? 'client needs' : 'clients need'} you today`;
            },
            getFacts() {
                const facts = [];
                const items = this.getClients.reduce((sum, client) => sum + client.items.length, 0);
                const followUps = this.getClients.reduce((sum, client) => sum + client.items.filter(i => i.isFollowUp).length, 0);

                if (items) facts.push(`${items} ${items === 1 ? 'problem' : 'problems'}`);
                if (followUps) facts.push(`${followUps} to follow up`);
                facts.push(`${this.getWaiting.length} waiting on someone`);

                const notYours = this.getNotYours.reduce((sum, owner) => sum + owner.count, 0);
                if (notYours) facts.push(`${notYours} with engineering or LevelCon`);

                return facts;
            }
        },
        methods: {
            formatDate(value) {
                return value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
            },
            ownerLabel(owner) {
                return OWNER_LABELS[owner] || owner;
            },
            followUpText(item) {
                const until = this.formatDate(item.snoozedUntil);
                const by = item.snoozedBy ? ` by ${item.snoozedBy}` : '';
                const isDue = new Date(item.snoozedUntil) > new Date();

                if (item.snoozeReason === SensorSnoozeReason.VisitScheduled)
                    return isDue
                        ? `Visit booked for ${until}${by}. Confirm with the installer today.`
                        : `Visit was booked for ${until}${by}. Check it happened and the sensor is back.`;

                if (item.snoozeReason === SensorSnoozeReason.WaitingOnClient)
                    return `Waiting on the client until ${until}${by}. Follow up today.`;

                return `Snoozed until ${until}${by}. Check whether it was fixed.`;
            },
            waitingText(item) {
                if (item.snoozedUntil && new Date(item.snoozedUntil) > new Date()) {
                    const by = item.snoozedBy ? ` · ${item.snoozedBy}` : '';
                    const until = this.formatDate(item.snoozedUntil);

                    if (item.snoozeReason === SensorSnoozeReason.VisitScheduled) return `Visit booked for ${until}${by}`;
                    if (item.snoozeReason === SensorSnoozeReason.WaitingOnClient) return `Waiting on the client until ${until}${by}`;
                    if (item.snoozeReason === SensorSnoozeReason.BatteryReplacement) return `Battery run, back in the queue ${until}${by}`;

                    return `${SENSOR_SNOOZE_REASON_LABELS[item.snoozeReason] || 'Snoozed'} until ${until}${by}`;
                }

                return `Marked handled${item.acknowledgedBy ? ` by ${item.acknowledgedBy}` : ''} ${this.formatDate(item.acknowledgedAt)}`;
            },
            rowFor(item) {
                return {
                    id: item.incidentId,
                    companyName: item.companyName,
                    batteryName: item.facilityName,
                    affectedSensorCount: item.affectedSensorCount,
                    sensorId: item.sensorId,
                    sensorName: item.sensorName,
                    levelconId: item.levelconId
                };
            },
            moreActions(item) {
                const row = this.rowFor(item);

                return {
                    'Open in LevelCon': item.levelconId ? () => this.$emit('open-levelcon', row) : false,
                    'Show in all incidents': () => this.$emit('show-incident', row),
                    Decommission: item.sensorId && item.primaryAction !== 'decommission' ? () => this.$emit('decommission', row) : false
                };
            },
            load() {
                this.isLoading = true;
                this.hasFailed = false;

                return tinyPumperIncidentsService
                    .today()
                    .then(today => {
                        this.today = today ?? null;
                    })
                    .catch(() => {
                        this.today = null;
                        this.hasFailed = true;
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<template>
    <div class="tp-today">
        <div v-if="hasFailed" class="alert alert-warning d-flex align-items-center gap-2" role="alert">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span class="flex-grow-1">Could not load today's queue. This is not an empty fleet.</span>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="load">Try again</button>
        </div>

        <div v-else-if="!today" class="tp-today__loading text-muted d-flex align-items-center gap-2" role="status">
            <span class="spinner-border spinner-border-sm"></span>
            Loading today's queue…
        </div>

        <template v-else>
            <header class="tp-today__summary">
                <div class="d-flex flex-wrap align-items-center gap-2">
                    <h2 class="tp-today__headline mb-0 me-auto">
                        <i v-if="isAllClear" class="bi bi-check-circle-fill text-success me-2"></i>
                        {{ getHeadline }}
                    </h2>
                    <button
                        v-if="today.isAlertingMuted"
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        title="Slack and the daily digest are off. Open the switch in All incidents."
                        @click="$emit('open-alerting')"
                    >
                        <i class="bi bi-bell-slash-fill me-1"></i>
                        Alerts muted
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="isLoading" @click="load">
                        <i class="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                    </button>
                </div>
                <p class="text-muted mb-0 mt-1">{{ getFacts.join(' · ') }}</p>
            </header>

            <div v-if="today.isVendorDown" class="alert alert-danger d-flex align-items-baseline gap-2 mt-3" role="alert">
                <i class="bi bi-cloud-slash-fill"></i>
                <span
                    ><b>LevelCon is not delivering.</b> Hold every visit until it clears: nothing below can be diagnosed while it
                    lasts.</span
                >
            </div>

            <section v-if="getClients.length" class="mt-4" aria-labelledby="tp-today-needs-you">
                <h3 id="tp-today-needs-you" class="tp-today__section-title">Needs you today</h3>

                <article v-for="client in getClients" :key="`tp-client-${client.companyName}`" class="card tp-today__client mb-3">
                    <div class="card-header d-flex flex-wrap align-items-baseline gap-2">
                        <h4 class="h5 mb-0 me-auto">{{ client.companyName }}</h4>
                        <small class="text-muted">
                            {{ client.items.length }} {{ client.items.length === 1 ? 'problem' : 'problems' }} ·
                            {{ client.affectedSensors }} {{ client.affectedSensors === 1 ? 'sensor' : 'sensors' }}
                        </small>
                    </div>

                    <ul class="list-group list-group-flush">
                        <li v-for="item in client.items" :key="`tp-item-${item.incidentId}`" class="list-group-item tp-today__item">
                            <div class="tp-today__item-body">
                                <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                                    <span v-if="item.isFollowUp" class="badge text-bg-warning">Follow up</span>
                                    <span v-else-if="item.isNew" class="badge text-bg-primary">New</span>
                                    <small class="text-muted">{{ item.facilityName || 'Whole client' }}</small>
                                </div>
                                <p class="fw-semibold mb-1">{{ item.headline }}</p>
                                <p v-if="item.isFollowUp" class="mb-1 tp-today__follow-up">
                                    <i class="bi bi-clock-history me-1"></i>
                                    {{ followUpText(item) }}
                                </p>
                                <p class="mb-0 text-body-secondary">
                                    <i class="bi bi-arrow-return-right me-1"></i>
                                    {{ item.nextStep }}
                                </p>
                                <p v-if="item.note" class="mb-0 mt-1 small text-muted">
                                    <i class="bi bi-sticky me-1"></i>
                                    {{ item.note }}
                                </p>
                            </div>

                            <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
                                <button
                                    v-if="item.primaryAction === 'decommission'"
                                    type="button"
                                    class="btn btn-sm btn-danger"
                                    :disabled="busyIncidentId === item.incidentId"
                                    @click="$emit('decommission', rowFor(item))"
                                >
                                    Decommission
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-sm"
                                    :class="item.primaryAction === 'call' ? 'btn-primary' : 'btn-outline-secondary'"
                                    :disabled="busyIncidentId === item.incidentId"
                                    @click="$emit('wait-on-client', rowFor(item))"
                                >
                                    <i v-if="item.primaryAction === 'call'" class="bi bi-telephone me-1"></i>
                                    {{ item.primaryAction === 'call' ? 'Client called' : 'Waiting on client' }}
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-sm"
                                    :class="item.primaryAction === 'visit' ? 'btn-primary' : 'btn-outline-secondary'"
                                    :disabled="busyIncidentId === item.incidentId"
                                    @click="$emit('book-visit', rowFor(item))"
                                >
                                    <i class="bi bi-calendar-check me-1"></i>
                                    Visit booked
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-success"
                                    :disabled="busyIncidentId === item.incidentId"
                                    @click="$emit('handled', rowFor(item))"
                                >
                                    <i class="bi bi-check2 me-1"></i>
                                    Handled
                                </button>
                                <ActionsDropdown
                                    :aria-label="`More actions for ${item.facilityName || client.companyName}`"
                                    :isLoading="busyIncidentId === item.incidentId"
                                    :actions="moreActions(item)"
                                />
                            </div>
                        </li>
                    </ul>
                </article>
            </section>

            <section v-else class="tp-today__clear mt-4 text-center text-muted">
                Every client's sensors are reporting, or someone is already on it.
            </section>

            <section v-if="getNotYours.length" class="mt-4" aria-labelledby="tp-today-not-yours">
                <h3 id="tp-today-not-yours" class="tp-today__section-title">With engineering or LevelCon</h3>
                <ul class="list-group">
                    <li
                        v-for="owner in getNotYours"
                        :key="`tp-owner-${owner.title}`"
                        class="list-group-item d-flex flex-wrap gap-2 align-items-baseline"
                    >
                        <span class="badge text-bg-light border">{{ ownerLabel(owner.owner) }}</span>
                        <span class="fw-semibold">{{ owner.title }}</span>
                        <span class="text-muted"
                            >{{ owner.count }} · {{ owner.affectedSensors }} {{ owner.affectedSensors === 1 ? 'sensor' : 'sensors' }}</span
                        >
                        <span class="w-100 small text-body-secondary">{{ owner.summary }}</span>
                    </li>
                </ul>
            </section>

            <section v-if="getWaiting.length" class="mt-4" aria-labelledby="tp-today-waiting">
                <button
                    id="tp-today-waiting"
                    type="button"
                    class="btn btn-link p-0 tp-today__section-title text-decoration-none"
                    :aria-expanded="isWaitingOpen"
                    @click="isWaitingOpen = !isWaitingOpen"
                >
                    <i :class="`bi ${isWaitingOpen ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`"></i>
                    Waiting on someone ({{ getWaiting.length }})
                </button>
                <ul v-if="isWaitingOpen" class="list-group mt-2">
                    <li v-for="item in getWaiting" :key="`tp-waiting-${item.incidentId}`" class="list-group-item">
                        <div class="d-flex flex-wrap gap-2 align-items-baseline">
                            <span class="fw-semibold">{{ item.companyName }}</span>
                            <small class="text-muted">{{ item.facilityName }}</small>
                            <small class="ms-auto text-muted">{{ waitingText(item) }}</small>
                        </div>
                        <div class="small">{{ item.headline }}</div>
                        <div v-if="item.note" class="small text-muted"><i class="bi bi-sticky me-1"></i>{{ item.note }}</div>
                    </li>
                </ul>
            </section>
        </template>
    </div>
</template>

<style lang="scss">
    .tp-today {
        max-width: 1100px;

        &__headline {
            font-size: 1.6rem;
            font-weight: 600;
        }

        &__section-title {
            font-size: 1.05rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            color: var(--bs-body-color);
        }

        &__item-body {
            max-width: 75ch;
        }

        &__follow-up {
            color: var(--bs-warning-text-emphasis);
        }

        &__clear {
            padding: 2.5rem 1rem;
            border: 1px dashed var(--bs-border-color);
            border-radius: var(--bs-border-radius-lg);
        }
    }
</style>
